// Inline creation of a master product variant inside the selected family.
// Prefilled from the current elimination picks: the ops person's failed
// search IS the spec of the missing variant. State and base unit are the
// only backend-required attributes; isActive is always true here.
// Origin/State/Brand/unit fields are dropdowns over the same canonical value
// lists the catalog's variant modal uses (spec stays free text) — inline
// creation must not mint non-canonical catalog values under time pressure.
// Field grid mirrors the elimination row above so columns line up; default
// control sizes throughout because ChakraSelect ignores size='sm' and sm
// inputs would sit shorter than the selects. Attribute multi-selects (one per
// EAV type bound to the family's category) sit between the identity row and
// Packaging.

import { useGetMasterProductFamilyApi } from 'apis/master-product-families';
import {
  CreateMasterProductPayload,
  EMPTY_UOM_OPTIONS,
  flattenAttributes,
  MasterProduct,
  useCreateMasterProductApi,
  useGetUomOptionsApi,
} from 'apis/master-products';
import {
  AttributeDictionaryType,
  dictionaryLabelFor,
  dictionaryValues,
  eavTypes,
  useGetAttributeDictionaryApi,
} from 'apis/product-attributes';
import { EMPTY_BRAND_OPTIONS, useGetBrandOptionsApi } from 'apis/product-brands';
import { BrandSelect } from 'components/brand-select';
import { Box, Button, ChakraSelect, Flex, FormControl, FormLabel, Grid, Input, Text } from 'design-system';
import { SpecTagsInput } from 'pages/inquiry-processor/components/family-workspace/spec-tags-input';
import { UOM_HELP } from 'pages/inquiry-processor/components/master-product-families/uom-field-meta';
import {
  buildCanonicalOptions,
  buildMultiCanonicalOptions,
  toCanonicalValue,
} from 'pages/inquiry-processor/utils/canonical-options';
import { plural, round } from 'pages/inquiry-processor/utils/describe-units';
import { apiErrorMessage } from 'pages/inquiry-processor/utils/inquiry-actions';
import { AttrSelections } from 'pages/inquiry-processor/utils/variant-elimination';
import { ReactNode, useEffect, useState } from 'react';

interface InlineVariantFormProps {
  familyId: number;
  familyName: string;
  initialValues: AttrSelections;
  onCreated: (product: MasterProduct) => void;
  onCancel: () => void;
}

export const InlineVariantForm = ({
  familyId,
  familyName,
  initialValues,
  onCreated,
  onCancel,
}: InlineVariantFormProps) => {
  const createProduct = useCreateMasterProductApi();
  // Scope the dictionary (state/origin values AND the attribute grid below) to
  // the family's category, matching the full variant modal. Until the family
  // resolves — or if its fetch fails — the param is undefined and the dictionary
  // is unscoped: today's exact behaviour, so the form degrades to the status
  // quo, never to a broken state. Works for both entry paths (family picked
  // from search, or created seconds ago by InlineFamilyForm) without touching
  // the wizard draft; the hook is react-query-cached and self-guards on id.
  const { data: family } = useGetMasterProductFamilyApi(familyId);
  const { data: dictionaryData } = useGetAttributeDictionaryApi({ catalogCategoryId: family?.catalogCategoryId });
  const dictionary = dictionaryData?.data;
  const stateValues = dictionaryValues(dictionary, 'state');
  const originValues = dictionaryValues(dictionary, 'origin');
  const { data: uomData, isError: isUomError } = useGetUomOptionsApi();
  const uomOptions = uomData ?? EMPTY_UOM_OPTIONS;
  const { data: brandData } = useGetBrandOptionsApi();
  const brandOptions = brandData ?? EMPTY_BRAND_OPTIONS;
  const [brand, setBrand] = useState(initialValues.brand ?? '');
  // Elimination picks are existing catalog strings — normalize case ('KG' →
  // 'kg') so the prefill lands on the canonical option instead of a legacy one.
  // The dictionary loads async, so the initial state is raw; it gets normalized
  // once the dictionary arrives (see useEffect below).
  const [origin, setOrigin] = useState(initialValues.origin ?? '');
  const [state, setState] = useState(initialValues.state ?? '');
  const [spec, setSpec] = useState(initialValues.spec ?? '');
  const [baseUnit, setBaseUnit] = useState(initialValues.baseUnit ?? '');
  // UOM dimensions are elimination attributes too — prefill them from the
  // picks the same way as brand/origin/state/spec.
  const [itemUnit, setItemUnit] = useState(initialValues.itemUnit ?? '');
  const [netContent, setNetContent] = useState(initialValues.netContent ?? '');
  const [packUnit, setPackUnit] = useState(initialValues.packUnit ?? '');
  const [packSize, setPackSize] = useState(initialValues.packSize ?? '');
  // EAV attributes, grouped per type (the drawer/modal shape). Always starts
  // empty: elimination picks (AttrSelections) cannot express an EAV value, so
  // there is nothing to prefill from.
  const [attributes, setAttributes] = useState<Record<string, string[]>>({});
  const [error, setError] = useState('');

  // Origin/State can't be canonicalized before the dictionary resolves (see the raw
  // useState above) — normalize once it arrives so the prefill lands on the canonical
  // option instead of staying a "(legacy)" entry.
  useEffect(() => {
    if (!dictionary) return;
    setOrigin((v) => toCanonicalValue(originValues, v));
    setState((v) => toCanonicalValue(stateValues, v));
    // originValues/stateValues intentionally excluded from deps: they're derived from
    // `dictionary` on every render, so including them would refire this effect every
    // render instead of once when the dictionary arrives. (No react-hooks eslint plugin
    // is configured in this repo, so no disable directive is needed here.)
  }, [dictionary]);

  // Same story as the dictionary effect above, for the UOM vocabulary: the options arrive
  // asynchronously, so the raw prefill is canonicalized once they land. Depending on
  // `uomOptions` directly is safe here (unlike originValues/stateValues) because it is the
  // query's own array, stable across renders, with EMPTY_UOM_OPTIONS as a stable fallback.
  useEffect(() => {
    if (!uomOptions.length) return;
    setBaseUnit((v) => toCanonicalValue(uomOptions, v));
    setItemUnit((v) => toCanonicalValue(uomOptions, v));
    setPackUnit((v) => toCanonicalValue(uomOptions, v));
  }, [uomOptions]);

  // And once more for the brand dictionary, which also arrives asynchronously.
  useEffect(() => {
    if (!brandOptions.length) return;
    setBrand((v) => toCanonicalValue(brandOptions, v));
  }, [brandOptions]);

  // Empty lists delete their key (the drawer's setValue semantics), so {}
  // stays the canonical "no attributes" state and save() sends nothing.
  const setAttribute = (key: string, values: string[]) => {
    setAttributes((prev) => {
      const next = { ...prev };
      if (values.length) next[key] = values;
      else delete next[key];
      return next;
    });
  };

  const attributeTypes = eavTypes(dictionary);

  // Picked-but-no-longer-offered types stay visible and clearable — the scoped
  // dictionary can REPLACE an unscoped first render (the picker pre-warms the
  // unscoped query, so the family fetch usually resolves after the grid first
  // paints), and a selection whose type vanished from the grid would otherwise
  // ride into the payload invisibly. Same recipe as variant-form-modal's
  // extraTypes.
  const attributeTypeKeys = new Set(attributeTypes.map((t) => t.key));
  const extraTypes: AttributeDictionaryType[] = Object.keys(attributes)
    .filter((key) => !attributeTypeKeys.has(key))
    .map((key) => ({ id: 0, key, label: key, isIdentityDefault: false, sortOrder: 0, values: [] }));
  const renderedTypes = [...attributeTypes, ...extraTypes];

  const canSave = !!state.trim() && !!baseUnit.trim();

  // Packaging sentence — same phrasing as describeUnits, but every noun is
  // the control that sets it (see the "Packaging" block in the JSX below).
  const netContentNum = netContent.trim() ? Number(netContent) : null;
  const packSizeNum = packSize.trim() ? Number(packSize) : null;
  const packageTotal =
    netContentNum != null && packSizeNum != null && baseUnit && packUnit
      ? `${round(netContentNum * packSizeNum)} ${baseUnit} per ${packUnit}`
      : null;

  const save = async () => {
    if (!canSave) return;
    setError('');
    const netContentToSave = netContent.trim() ? Number(netContent) : undefined;
    if (netContentToSave !== undefined && (!Number.isFinite(netContentToSave) || netContentToSave <= 0)) {
      setError(`${UOM_HELP.netContent.label} must be a positive number`);
      return;
    }
    const packSizeToSave = packSize.trim() ? Number(packSize) : undefined;
    if (packSizeToSave !== undefined && (!Number.isInteger(packSizeToSave) || packSizeToSave <= 0)) {
      setError(`${UOM_HELP.packSize.label} must be a positive whole number`);
      return;
    }
    const payload: CreateMasterProductPayload = {
      familyId,
      state: state.trim(),
      baseUnit: baseUnit.trim(),
      isActive: true,
    };
    if (brand.trim()) payload.brand = brand.trim();
    if (origin.trim()) payload.origin = origin.trim();
    if (spec.trim()) payload.spec = spec.trim();
    if (itemUnit.trim()) payload.itemUnit = itemUnit.trim();
    if (netContentToSave !== undefined) payload.netContent = netContentToSave;
    if (packUnit.trim()) payload.packUnit = packUnit.trim();
    if (packSizeToSave !== undefined) payload.packSize = packSizeToSave;
    const attributesPayload = flattenAttributes(attributes);
    if (attributesPayload.length) payload.attributes = attributesPayload;
    try {
      const product = await createProduct.mutateAsync(payload);
      onCreated(product);
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  };

  const fieldLabel = (label: ReactNode) => (
    <FormLabel fontSize='xs' mb='1' color='gray.600'>
      {label}
    </FormLabel>
  );

  const selectField = (
    label: ReactNode,
    canon: readonly string[],
    value: string,
    set: (v: string) => void,
    placeholder: string,
    required = false,
    labelFor?: (v: string) => string
  ) => {
    const options = buildCanonicalOptions(canon, value, labelFor);
    const selected = options.find((o) => o.value === value) ?? null;
    return (
      <FormControl isRequired={required}>
        {fieldLabel(label)}
        <ChakraSelect
          options={options}
          value={selected}
          onChange={(opt: any) => set(opt?.value ?? '')}
          placeholder={placeholder}
          isClearable
          menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
          menuPosition='fixed'
          styles={{ menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 2000 }) }}
        />
      </FormControl>
    );
  };

  // Label-less versions of the controls above, for the inline "Packaging"
  // sentence — the surrounding words are the label, so a FormLabel above each
  // one would just repeat it.
  const bareSelect = (
    canon: readonly string[],
    value: string,
    set: (v: string) => void,
    placeholder: string,
    labelFor?: (v: string) => string
  ) => {
    const options = buildCanonicalOptions(canon, value, labelFor);
    const selected = options.find((o) => o.value === value) ?? null;
    return (
      <ChakraSelect
        options={options}
        value={selected}
        onChange={(opt: any) => set(opt?.value ?? '')}
        placeholder={placeholder}
        isClearable
        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
        menuPosition='fixed'
        styles={{ menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 2000 }) }}
      />
    );
  };

  const bareInput = (value: string, set: (v: string) => void, numeric: 'decimal' | 'integer') => (
    <Input
      bg='white'
      type='number'
      min={0}
      step={numeric === 'integer' ? 1 : 'any'}
      value={value}
      onChange={(e) => set(e.target.value)}
    />
  );

  return (
    <Box borderWidth='1px' borderColor='red.200' rounded='md' p='3' bg='red.50'>
      <Text fontWeight='semibold' fontSize='sm' mb='1'>
        New variant in “{familyName}”
      </Text>
      <Text fontSize='xs' color='gray.600' mb='2'>
        This creates a master catalog entry visible everywhere.
      </Text>
      <Grid templateColumns={{ base: '1fr 1fr', md: 'repeat(4, 1fr)' }} gap='3'>
        <FormControl>
          {fieldLabel('Brand')}
          <BrandSelect
            options={buildCanonicalOptions(brandOptions, brand)}
            value={buildCanonicalOptions(brandOptions, brand).find((o) => o.value === brand) ?? null}
            onBrandChange={setBrand}
            placeholder='Select brand'
            isClearable
            menuPortalZIndex={2000}
          />
        </FormControl>
        {selectField('Origin', originValues, origin, setOrigin, 'Search country…')}
        {selectField(
          'State',
          stateValues,
          state,
          setState,
          'Select state',
          true,
          dictionaryLabelFor(dictionary, 'state')
        )}
        <FormControl>
          {fieldLabel('Spec')}
          <SpecTagsInput value={spec} onChange={setSpec} bg='white' />
        </FormControl>
      </Grid>
      {(attributeTypes.length > 0 || extraTypes.length > 0) && (
        <Grid
          data-testid='inline-attribute-fields'
          templateColumns={{ base: '1fr 1fr', md: 'repeat(4, 1fr)' }}
          gap='3'
          mt='3'>
          {renderedTypes.map((type) => {
            const current = attributes[type.key] ?? [];
            const options = buildMultiCanonicalOptions(
              type.values.map((v) => v.value),
              current,
              dictionaryLabelFor(dictionary, type.key)
            );
            const selected = options.filter((o) => current.includes(o.value));
            return (
              <FormControl key={type.key}>
                {fieldLabel(type.label)}
                <ChakraSelect
                  isMulti
                  options={options}
                  value={selected}
                  onChange={(opts: any) =>
                    setAttribute(
                      type.key,
                      ((opts as { value: string }[]) ?? []).map((o) => o.value)
                    )
                  }
                  placeholder={`Select ${type.label.toLowerCase()}`}
                  isClearable
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                  menuPosition='fixed'
                  styles={{ menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 2000 }) }}
                />
              </FormControl>
            );
          })}
        </Grid>
      )}
      <Box mt='3'>
        <FormLabel fontSize='xs' mb='1' color='gray.600'>
          Packaging
        </FormLabel>
        <Flex wrap='wrap' align='center' gap='2' fontSize='sm'>
          <Text>1</Text>
          <Box w={{ base: '45%', md: '130px' }}>{bareSelect(uomOptions, packUnit, setPackUnit, 'e.g. case')}</Box>
          <Text>contains</Text>
          <Box w={{ base: '28%', md: '70px' }}>{bareInput(packSize, setPackSize, 'integer')}</Box>
          <Box w={{ base: '45%', md: '130px' }}>
            {bareSelect(uomOptions, itemUnit, setItemUnit, 'e.g. piece', (v) => plural(v, packSizeNum ?? 2))}
          </Box>
          <Text>of</Text>
          <Box w={{ base: '28%', md: '70px' }}>{bareInput(netContent, setNetContent, 'decimal')}</Box>
          <Flex align='center' gap='0.5'>
            <Box w='110px'>{bareSelect(uomOptions, baseUnit, setBaseUnit, 'e.g. kg')}</Box>
            <Text color='red.500'>*</Text>
          </Flex>
          <Text>each{packageTotal ? '' : '.'}</Text>
          {packageTotal && (
            <>
              <Text>—</Text>
              <Text fontWeight='600'>{packageTotal}.</Text>
            </>
          )}
        </Flex>
        <Text fontSize='xs' color='gray.500' mt='1'>
          <Text as='span' color='red.500'>
            *
          </Text>{' '}
          {UOM_HELP.baseUnit.label} is required — it&apos;s what lets us compare prices across variants. The rest
          describe how it&apos;s packaged and are optional.
        </Text>
      </Box>
      <Flex gap='2' mt='3'>
        <Button size='sm' variant='outline' onClick={onCancel} isDisabled={createProduct.isLoading}>
          Cancel
        </Button>
        <Button
          size='sm'
          colorScheme='red'
          onClick={save}
          isLoading={createProduct.isLoading}
          loadingText='Creating…'
          isDisabled={!canSave}>
          Create variant
        </Button>
      </Flex>
      {isUomError && (
        <Text fontSize='xs' color='red.500' mt='2'>
          Couldn&apos;t load units — reload the page to try again.
        </Text>
      )}
      {error && (
        <Text fontSize='xs' color='red.500' mt='2' whiteSpace='pre-wrap'>
          {error}
        </Text>
      )}
    </Box>
  );
};
